/**
 * `helper/customerHelpers` — the COMMON half.
 *
 * 🔴 **This file held no code until 2026-09-05.** The two symbols core had were disjoint —
 * `parseCustomerRecord` on web, `filterAddresses` on mobile — because each app's `CustomerSlice`
 * asked for its own. That stopped being true when the two slices converged: the shared
 * `getCustomerRecord` / `setCustomerDataSuccess` use BOTH, so both live here.
 *
 * All of it is pure — arguments in, answer out, no store and no platform API — EXCEPT
 * `fetchDetailedCustomerInfo`, which dispatches. See its note.
 *
 * Most of what remains in the apps' `customerHelpers` reads the app's redux `store` singleton,
 * which `packages/core` cannot import — see the plan's §4/§6 store phase.
 */
import store from "../../redux-store";
import { getDetailedCustomerInfo } from "../../redux-store/slices/CustomerSlice";
import { isEmpty } from "lodash";

/** Whether customer details identify a saved customer by ID or phone number. */
export function checkForCustInfo(customerInfo: any = {}) {
  return (
    !isEmpty(customerInfo) &&
    !!(customerInfo?.customerId || customerInfo?.contactInfo?.phoneNumber)
  );
}

/**
 * Dispatch a fetch of full customer detail — the record, its `loyaltyInfo` and its
 * `providerConsents`. No-ops offline, where the endpoint is unreachable.
 *
 * ⚠️ **`isExternal` decides which identifier `refId` IS.** `false`/omitted means OUR
 * `customerRefId`; `true` means the customer's phone number at the provider. Only the first
 * makes `saga/pos/customer` stamp `externalLoyaltyData.customerRefId`, which is what
 * `ensureCustomerLoyaltyInfo` reads to tell "we have loyalty info" from "we have THIS
 * customer's loyalty info".
 *
 * SHARED, not a web body: `getDetailedCustomerInfo` is a shared reducer and the offline read is
 * the same on both platforms. It sat in `index.desktop.ts` for a day because web's app file is
 * where it came from — which is a fact about its origin, not about the platform.
 *
 * ⚠️ It reads the store, so unlike everything below it is NOT pure. That is why it is the only
 * thing in this file that does, and why the read happens inside the function.
 */
export function fetchDetailedCustomerInfo(refId: string | number, isExternal?: boolean) {
  if (store.getState().businessStore?.isOffline) return;
  store.dispatch(getDetailedCustomerInfo({ ref: refId, external: isExternal }));
}

/**
 * Merges a customer payload with the existing record.
 *
 * `providerConsents` is destructured out and intentionally DROPPED — it is not spread into
 * the returned customer. Preserved verbatim from the app.
 */
export const parseCustomerRecord = (
  payload: any,
  existingCustomerInfo: any
) => {
  const { loyaltyInfo, providerConsents, ...customer } = payload;

  // if isExternal is true, it means loyaltyInfo is coming from external provider and should not be merged with existing customer info.
  if (!loyaltyInfo?.isExternal) {
    customer.loyaltyInfo = loyaltyInfo;
  }

  if (customer.mid) {
    customer.redemptionScheme =
      payload.redemptionScheme || existingCustomerInfo?.redemptionScheme;

    customer.membershipName =
      payload.membershipName || existingCustomerInfo?.membershipName;
  }

  return customer;
};

/** Drops addresses with an empty `addressLine`; returns the input untouched if there are none. */
export function filterAddresses(customerData: any) {
  //filter incorrect addresses.
  if (customerData?.addresses?.length) {
    let addresses = customerData?.addresses?.filter(
      (address: any) => address?.addressLine?.length > 0,
    );
    return { ...customerData, addresses };
  }
  return customerData;
}

/**
 * Drop the provider-owned fields from a customer payload before it becomes `customerInfo`.
 *
 * `providerConsents` is destructured out and intentionally DROPPED, and an EXTERNAL `loyaltyInfo`
 * is not merged either: both belong to `customerStore.externalLoyaltyData`, which the loyalty
 * consent flow reads. Keeping a second copy on the customer record is what lets the two drift.
 *
 * ⚠️ Deliberately NOT the same test as `parseCustomerRecord`'s. That one writes
 * `customer.loyaltyInfo` whenever the payload's is not external — including writing `undefined`
 * when there is none; this one only writes a loyaltyInfo that actually exists. Both are the
 * apps' own behaviour, preserved verbatim; do not "simplify" them into one.
 */
export function stripProviderOwnedFields(payload: any) {
  const { loyaltyInfo, providerConsents, ...rest } = payload || {};
  if (loyaltyInfo && !loyaltyInfo?.isExternal) {
    rest.loyaltyInfo = loyaltyInfo;
  }
  return rest;
}

export function prepareCommonCustomerData(data: any) {
  const customerData: any = {
    ...data,
    addresses: data?.addresses,
    contactInfo: data?.contactInfo,
    customerId: data?.customerId,
    birthDate: data?.birthDate,
    anniversaryDate: data?.anniversaryDate,
    taxId: data?.taxId,
    taxStateCode: data?.taxStateCode,
    gender: data?.gender,
    customerRefId: data?.mid,
    companyRefId: data?.companyRefId,
    companyName: data?.companyName,
  };
  if (data?.membershipId) {
    customerData.memberships = [
      {
        membershipId: data?.membershipId,
        start: data?.membershipStart || null,
        end: data?.membershipEnd || null,
      },
    ];
  }

  if (customerData?.loyaltyInfo?.isExternal) {
    delete customerData.loyaltyInfo;
    delete customerData.providerConsents;
  }

  const membershipFields = ['membershipStart', 'membershipEnd'];

  membershipFields.forEach((key) => {
    if (customerData[key] == null) {
      delete customerData[key];
    }
  });

  delete customerData.redemptionScheme;
  delete customerData.membershipId;
  delete customerData.membershipName;
  delete customerData.membershipStart;
  delete customerData.membershipEnd;

  return customerData;
}

/**
 * `9876543210` → `9********0`. The masking both apps show above an OTP field.
 *
 * SHARED: the two apps' `maskPhoneNumber`/`getMaskedCustomerNumber` bodies mask identically —
 * everything but the first character and the last two is replaced, using `substr` on the ORIGINAL
 * string as the `replace` needle. That is transcribed rather than rewritten: a number with a
 * repeating middle would otherwise mask a different span, and this is what the POS has shown for
 * years.
 *
 * ⚠️ Only the SOURCE of the number diverges (see each variant's `getMaskedCustomerNumber`), which
 * is why the masking lives here and the store read does not.
 */
export function maskPhoneNumber(phoneNumber: string): string {
  if (!phoneNumber) return "";
  const middle = phoneNumber.substr(1, phoneNumber.length - 3);
  return phoneNumber.replace(middle, middle.replace(/./g, "*"));
}
